import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, getAuthToken } from '@/lib/api';

/*
 * Data layer for the own-booking admin (Tilgjengelighet + Bookinger). These
 * endpoints live under /api/dashboard/booking/* behind the Google session, but
 * aren't in @nono/contract yet, so we fetch them directly with the same
 * semantics as the generated client (same-origin cookie + Bearer token).
 */

export type BookingWeekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type BookingDayHours = { open: boolean; fromMin: number; toMin: number };
export type BookingWeeklyHours = Record<BookingWeekday, BookingDayHours>;

export interface BookingRules {
  id: number;
  openMon: boolean;
  openTue: boolean;
  openWed: boolean;
  openThu: boolean;
  openFri: boolean;
  openSat: boolean;
  openFromMin: number;
  openToMin: number;
  /** Explicit activation gate. False = legacy global hours remain live. */
  weeklyHoursActive: boolean;
  /** Editable draft while inactive; effective weekly grid while active. */
  weeklyHours: BookingWeeklyHours;
  intervalMin: number;
  defaultCapacity: number;
  sessionLenMin: number;
  bufferStartMin: number;
  bufferEndMin: number;
  horizonDays: number;
  minNoticeHours: number;
  updatedAt: string;
}

export type BookingStatus = 'new' | 'synced' | 'error' | 'cancelled';

export interface BookingRow {
  id: number;
  ref: string;
  name: string;
  phone: string;
  email: string;
  slotStartsAt: string;
  status: BookingStatus;
  syncedBy: string | null;
  syncedAt: string | null;
  errorReason: string | null;
  // Momence customer auto-create result (set on the row by the backend right
  // after booking). momenceMemberId present → customer ready to pick in Momence.
  momenceMemberId: number | null;
  momenceMemberError: string | null;
  intake: Record<string, unknown> | null;
  // Ad attribution captured at booking time. pageUrl carries the landing URL's
  // UTM/ad params (utm_content=ad name, ad_id, utm_campaign, utm_term=ad set…);
  // fbclid/capiStatus tell whether it came from a Meta ad + whether the
  // person-level CAPI Schedule reached Meta. Parsed for display in the queue.
  pageUrl: string | null;
  fbclid: string | null;
  capiStatus: string | null;
  // Quiz-økta bookingen kom ut av, og hvor langt DEN økta faktisk kom.
  // Økt-ID-en lages når quiz-siden lastes, så den alene beviser ingenting;
  // quizMaxStep er serverens egen måling (0 = landet uten å starte,
  // 1-8 = svarte på spørsmål, 10 = nådde resultatet). null = ingen quiz-økt.
  quizSessionId: string | null;
  quizMaxStep: number | null;
  createdAt: string;
  // Soft-archive: null = active (in the queue), set = filed away (hidden from
  // the active views, shown under the "Arkivert" filter, restorable).
  archivedAt: string | null;
}

export interface BlockedDate {
  id: number;
  date: string;
  note: string | null;
}

export interface WeekSlot {
  startsAt: string;
  time: string;
  capacity: number;
  booked: number;
  occupied: number;
  remaining: number;
  blocked: boolean;
  hasOverride: boolean;
  outsideHours: boolean;
}

export type ScheduleConflictReason =
  | 'closed-day'
  | 'before-hours'
  | 'after-hours'
  | 'off-grid'
  | 'over-capacity';

export interface ScheduleConflict {
  id: number;
  ref: string;
  name: string;
  status: BookingStatus;
  slotStartsAt: string;
  localDate: string;
  localTime: string;
  reason: ScheduleConflictReason;
  dayBlocked: boolean;
  slotOverrideBlocked: boolean;
  slotOverrideCapacity: number | null;
}

export interface SchedulePreflight {
  ok: boolean;
  clear: boolean;
  checkedAt: string;
  futureConfirmedBookings: number;
  conflicts: ScheduleConflict[];
  /** Conflicts that stop the save — today only capacity breaches. */
  blockingConflicts?: number;
  /** Bookings the server keeps and shows as «Utenfor åpningstid». */
  retainedConflicts?: number;
  validationError?: string;
}
export interface WeekDay {
  date: string;
  weekday: number;
  closed: boolean;
  dayBlocked: boolean;
  dayCapacity: number | null;
  slots: WeekSlot[];
}

async function authed<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  const token = getAuthToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init?.body) headers.set('Content-Type', 'application/json');
  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const j = (await res.json()) as { error?: unknown };
      if (typeof j.error === 'string') msg = j.error;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, msg);
  }
  return (await res.json()) as T;
}

// --- Tilgjengelighet (availability rules + day blocks) ---
export function useBookingRules() {
  return useQuery({
    queryKey: ['booking-rules'],
    queryFn: () => authed<{ ok: boolean; rules: BookingRules }>('/api/dashboard/booking/rules'),
    staleTime: 60_000,
  });
}

export function usePutBookingRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      patch: Partial<Omit<BookingRules, 'id' | 'updatedAt'>> & {
        /** The mode the screen showed; the server refuses if it has changed. */
        expectWeeklyHoursActive?: boolean;
      },
    ) =>
      authed<{ ok: boolean; rules: BookingRules }>('/api/dashboard/booking/rules', {
        method: 'PUT',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['booking-rules'] });
      void qc.invalidateQueries({ queryKey: ['booking-week'] });
    },
    // A refused write usually means the screen is behind the server (another
    // tab, another device): fetch the truth so the next attempt starts from it.
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['booking-rules'] });
    },
  });
}

/** Read-only dry run of a rules change. {weeklyHours} rehearses the weekly plan
 * as the live schedule; {weeklyHoursActive:false} rehearses the return to the
 * shared hours. Neither writes anything. */
export function useBookingRulesPreflight() {
  return useMutation({
    mutationFn: (body: { weeklyHours: BookingWeeklyHours } | { weeklyHoursActive: false }) =>
      authed<SchedulePreflight>('/api/dashboard/booking/rules/preflight', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

export function useBlockedDates() {
  return useQuery({
    queryKey: ['booking-blocked-dates'],
    queryFn: () =>
      authed<{ ok: boolean; dates: BlockedDate[] }>('/api/dashboard/booking/blocked-dates'),
    staleTime: 60_000,
  });
}

export function useSetDateBlocked() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { date: string; blocked: boolean }) =>
      authed<{ ok: boolean; date: string; blocked: boolean; existingBookings: number }>(
        '/api/dashboard/booking/blocked-dates',
        { method: 'POST', body: JSON.stringify(vars) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['booking-blocked-dates'] });
      void qc.invalidateQueries({ queryKey: ['booking-week'] });
      void qc.invalidateQueries({ queryKey: ['booking-rules'] });
    },
  });
}

// --- Tilgjengelighet week grid ---
export function useWeekGrid(from: string) {
  return useQuery({
    queryKey: ['booking-week', from],
    queryFn: () =>
      authed<{ ok: boolean; from: string; days: WeekDay[] }>(
        `/api/dashboard/booking/week?from=${encodeURIComponent(from)}`,
      ),
    enabled: Boolean(from),
    staleTime: 30_000,
  });
}

export function useSetSlotOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { slotStartsAt: string; capacity?: number | null; blocked?: boolean }) =>
      authed<{ ok: boolean }>('/api/dashboard/booking/slot-override', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['booking-week'] }),
  });
}

export function useSetDayCapacity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { date: string; capacity: number | null }) =>
      authed<{ ok: boolean }>('/api/dashboard/booking/day-capacity', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['booking-week'] }),
  });
}

// --- Bookinger (queue + sync actions) ---
// Fetch ALL bookings once; the view filters by status/search/date client-side
// (so the filter chips can show counts, like the design).
export function useBookingQueue() {
  return useQuery({
    queryKey: ['booking-queue'],
    queryFn: () =>
      authed<{ ok: boolean; bookings: BookingRow[] }>(
        '/api/dashboard/booking/queue?status=all&limit=1000',
      ),
    staleTime: 30_000,
    refetchInterval: 60_000, // bookings land as customers book
  });
}

export function useSetSyncStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; status: BookingStatus; errorReason?: string }) =>
      authed<{ ok: boolean; booking: BookingRow }>(
        `/api/dashboard/booking/queue/${vars.id}/sync-status`,
        {
          method: 'POST',
          body: JSON.stringify({ status: vars.status, errorReason: vars.errorReason }),
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['booking-queue'] });
    },
  });
}

// Arkiver / Gjenopprett — soft-archive (recoverable). archived:false restores.
export function useArchiveBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; archived: boolean }) =>
      authed<{ ok: boolean; booking: BookingRow }>(
        `/api/dashboard/booking/queue/${vars.id}/archive`,
        { method: 'POST', body: JSON.stringify({ archived: vars.archived }) },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['booking-queue'] }),
  });
}

// --- Attribution reconciliation: confirmed-click bookings vs Meta Schedule ---
export interface ReconciliationData {
  days: number;
  from: string;
  to: string;
  bookings: { total: number; confirmedFromAd: number; direct: number };
  metaScheduleConversions: number;
  gap: number;
}
export function useReconciliation(days: number) {
  return useQuery({
    queryKey: ['booking-reconciliation', days],
    queryFn: () =>
      authed<{ ok: boolean } & ReconciliationData>(
        `/api/dashboard/booking/reconciliation?days=${days}`,
      ),
    staleTime: 60_000,
  });
}

// Slett — permanent hard delete (irreversible).
export function useDeleteBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number }) =>
      authed<{ ok: boolean; deleted: boolean }>(`/api/dashboard/booking/queue/${vars.id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['booking-queue'] }),
  });
}
